<template>
  <!-- Import Modal -->
  <ImportModal
    v-if="showImportModal"
    :title="t('campaignsBulkList.import.title')"
    import-type="campaign_bulk"
    @close="showImportModal = false"
  />

  <!-- Create Campaign Modal -->
  <CampaignsBulkBaseSelectorV2
    v-if="showCreateModal"
    @on-close-dialog="showCreateModal = false"
  />

  <!-- Action Components -->
  <DuplicateBulkCampaignAction
    ref="duplicateRef"
    @confirm="onActionConfirm"
  />
  <DeleteBulkCampaignAction
    ref="deleteRef"
    @confirm="onActionConfirm"
  />
  <ArchiveBulkCampaignAction
    ref="archiveRef"
    @confirm="onActionConfirm"
  />
  <SuspendBulkCampaignAction
    ref="suspendRef"
    @confirm="onActionConfirm"
  />
  <PublishToGalleryDialog
    ref="publishGalleryRef"
    @confirm="onPublishToGalleryConfirm"
  />

  <!-- Need Offer Modal -->
  <Dialog
    v-model:visible="showNeedOfferModal"
    modal
    :closable="true"
    :style="{ width: '50vw' }"
    :breakpoints="{ '960px': '75vw', '640px': '100vw' }"
  >
    <template #header>
      <h3>{{ t('attention') }}</h3>
    </template>
    <section class="contents">
      <div class="grid col-12 text-left">
        {{ t('campaigns.bulk.messages.insufficientOffer') }}
      </div>
    </section>
    <template #footer>
      <SpmButton
        :label="t('close')"
        icon="far fa-times"
        class-style="p-button p-button-secondary"
        @click="showNeedOfferModal = false"
      />
      <SpmButton
        :label="t('offers.updatePlanButton')"
        icon="fa-regular fa-edit"
        class-style="p-button p-button-primary"
        @click="goToMyOffer"
      />
    </template>
  </Dialog>

  <!-- Confirmation Dialog for editing scheduled campaigns -->
  <ConfirmDialog group="bulkCampaignsConfirm" />

  <div class="campaigns-bulk-page">
    <PageHeader
      :title="t('campaignsBulkList.title')"
      :summary="t('campaignsBulkList.summary')"
    >
      <template #actions>
        <SpmButton
          :label="t('automatedScenarios.actions.import')"
          icon="fa fa-file-import"
          class-style="p-button p-button-secondary"
          @click="showImportModal = true"
        />
        <SpmButton
          :label="t('campaignsBulkList.actions.create')"
          icon="fa fa-plus"
          class-style="p-button p-button-primary"
          @click="showCreateModal = true"
        />
      </template>
    </PageHeader>

    <!-- Content Section with DataTable -->
    <div class="page-content">
      <SpmTableAPI
        v-if="apiCall"
        ref="tableRef"
        :api-call="apiCall"
        :columns="columns"
        :show-reload-button="true"
        data-key="id"
        :row-actions-options="rowActions"
        :enable-grouped-actions="true"
        :grouped-actions-options="groupedActions"
        :persistent-filters="persistentFilters"
        :initial-sort="initialSort"
        :show-export-all-button="false"
        :exportable="true"
        expandable
        :export-config="{ entityType: 'campaign_bulk' }"
        :lazy-load-fn="loadCampaignsStats"
        :lazy-refresh-interval="120000"
        @export="handleExport"
      >
        <template #empty>
          <SpmEmptyState
            icon="fa-regular fa-paper-plane"
            :title="t('campaignsBulkList.emptyState.title')"
            :description="t('campaignsBulkList.emptyState.description')"
            :button-label="t('campaignsBulkList.actions.create')"
            button-icon="fa fa-plus"
            @action="showCreateModal = true"
          />
        </template>

        <!-- Name column with meta info -->
        <template #cell-name="{ data }">
          <div class="campaign-info">
            <div class="campaign-name-line">
              <IntegrationOwnerBadge
                v-if="data.owner_integration"
                :owner-integration="data.owner_integration"
              />
              <span
                v-tooltip.top="data.name"
                class="campaign-name font-bold"
              >{{ data.name }}</span>
            </div>
            <div class="campaign-meta">
              <span v-tooltip.top="t('campaignsBulkList.dateTooltips.creation')">
                <SpmDateDisplay
                  :value="data.date_creation"
                  format="datetime"
                  icon="fa-light fa-calendar-circle-plus"
                  size="sm"
                />
              </span>
              <span
                v-if="!data.date_send"
                v-tooltip.top="getModificationTooltip(data.status)"
              >
                <SpmDateDisplay
                  :value="data.date_modification"
                  format="datetime"
                  icon="fa-light fa-edit"
                  size="sm"
                />
              </span>
              <span
                v-if="data.date_send"
                v-tooltip.top="t('campaignsBulkList.dateTooltips.send')"
              >
                <SpmDateDisplay
                  :value="data.date_send"
                  format="datetime"
                  icon="fa-light fa-paper-plane"
                  size="sm"
                  :timezone="clientTimezone"
                />
              </span>
            </div>
            <div
              v-if="extractChannelTags(data).length"
              class="campaign-tags"
            >
              <SpmBadge
                v-for="channel in extractChannelTags(data)"
                :key="`${data.id}-${channel}`"
                :label="formatChannel(channel)"
                :icon="getChannelIcon(channel)"
                :colors="getChannelTagStyle(channel)"
                size="sm"
              />
            </div>
          </div>
        </template>

        <!-- Status column -->
        <template #cell-status="{ data }">
          <div class="status-cell">
            <SpmBadge
              :label="t(`campaignsBulkList.status.${data.status}`)"
              :icon="getStatusIcon(data.status)"
              :colors="getStatusStyle(data.status)"
            />
            <span
              v-if="data.send_mode && data.send_mode !== 'standard'"
              v-tooltip="sendModeTooltip(data)"
              class="send-mode-badge"
              :class="`send-mode-badge--${data.send_mode}`"
            >
              <i :class="sendModeIcon(data.send_mode)" aria-hidden="true" />
              <span>{{ sendModeLabel(data) }}</span>
            </span>
          </div>
        </template>

        <!-- Performance column -->
        <template #cell-performance="{ data }">
          <div class="metrics-display">
            <SpmMetricDisplay
              :label="t('campaignsBulkList.metrics.openRate')"
              :help="t('campaigns.common.metrics.openRateUniqueHelp')"
              :value="data.open_rate"
              type="percentage"
              :is-decimal="false"
            />
            <SpmMetricDisplay
              :label="t('campaigns.common.metrics.openRateTotal')"
              :help="t('campaigns.common.metrics.openRateTotalHelp')"
              :value="data.open_rate_total"
              type="percentage"
              :is-decimal="false"
            />
            <SpmMetricDisplay
              :label="t('campaignsBulkList.metrics.clickRate')"
              :value="data.click_rate"
              type="percentage"
              :is-decimal="false"
            />
          </div>
        </template>

        <!-- Conversions column -->
        <template #cell-conversions="{ data }">
          <div class="metrics-display">
            <SpmMetricDisplay
              :label="t('campaignsBulkList.metrics.salesAmount')"
              :value="data.sales_amount"
              type="currency"
            />
            <SpmMetricDisplay
              :label="t('campaignsBulkList.metrics.conversionRate')"
              :value="data.conversion_rate"
              type="percentage"
              :is-decimal="false"
            />
          </div>
        </template>

        <!-- Expansion row with detailed metrics -->
        <template #expansion="{ data }">
          <div class="expansion-container">
            <!-- Summary Cards Row -->
            <div class="summary-cards">
              <div class="summary-card summary-card--primary">
                <div class="summary-card__icon">
                  <i class="fa-solid fa-paper-plane" />
                </div>
                <div class="summary-card__content">
                  <span class="summary-card__value">{{ formatInteger(data.deliveries) }}</span>
                  <span class="summary-card__label">{{ t('campaignsBulkList.expansion.deliveries') }}</span>
                </div>
              </div>
              <div class="summary-card summary-card--success">
                <div class="summary-card__icon">
                  <i class="fa-solid fa-envelope-open" />
                </div>
                <div class="summary-card__content">
                  <span class="summary-card__value">{{ formatPercentage(data.open_rate) }}</span>
                  <span class="summary-card__label">
                    {{ t('campaignsBulkList.metrics.openRate') }}
                    <SpmHelpIcon :text="t('campaigns.common.metrics.openRateUniqueHelp')" />
                  </span>
                </div>
              </div>
              <div class="summary-card summary-card--success">
                <div class="summary-card__icon">
                  <i class="fa-solid fa-envelope-open-text" />
                </div>
                <div class="summary-card__content">
                  <span class="summary-card__value">{{ formatPercentage(data.open_rate_total) }}</span>
                  <span class="summary-card__label">
                    {{ t('campaigns.common.metrics.openRateTotal') }}
                    <SpmHelpIcon :text="t('campaigns.common.metrics.openRateTotalHelp')" />
                  </span>
                </div>
              </div>
              <div class="summary-card summary-card--info">
                <div class="summary-card__icon">
                  <i class="fa-solid fa-mouse-pointer" />
                </div>
                <div class="summary-card__content">
                  <span class="summary-card__value">{{ formatPercentage(data.click_rate) }}</span>
                  <span class="summary-card__label">{{ t('campaignsBulkList.metrics.clickRate') }}</span>
                </div>
              </div>
              <div class="summary-card summary-card--warning">
                <div class="summary-card__icon">
                  <i class="fa-solid fa-shopping-cart" />
                </div>
                <div class="summary-card__content">
                  <span class="summary-card__value">{{ formatAmount(data.sales_amount) }}</span>
                  <span class="summary-card__label">{{ t('campaignsBulkList.metrics.salesAmount') }}</span>
                </div>
              </div>
            </div>

            <!-- Compact Metrics Row -->
            <div class="metrics-row">
              <div class="metric-item">
                <span class="metric-item__label">{{ t('campaignsBulkList.expansion.opens') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.opens) }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-item__label">{{ t('campaignsBulkList.expansion.openUnique') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.open_unique) }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-item__label">{{ t('campaignsBulkList.expansion.clicks') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.clicks) }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-item__label">{{ t('campaignsBulkList.expansion.clickUnique') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.click_unique) }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-item__label">{{ t('campaignsBulkList.expansion.sales') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.sales) }}</span>
              </div>
              <div class="metric-item metric-item--warning">
                <span class="metric-item__label">{{ t('campaignsBulkList.expansion.bounces') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.bounces) }}</span>
              </div>
              <div class="metric-item metric-item--danger">
                <span class="metric-item__label">{{ t('campaignsBulkList.expansion.spam') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.spam) }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-item__label">{{ t('campaignsBulkList.expansion.unsubscribes') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.unsubscribes) }}</span>
              </div>
            </div>

            <!-- Channel Stats (inline display) -->
            <div
              v-if="data.channels?.length"
              class="channels-inline"
            >
              <div
                v-for="channel in data.channels"
                :key="`${data.id}-${channel.channel}`"
                class="channel-inline"
              >
                <div class="channel-inline__header">
                  <i
                    v-if="getChannelIcon(channel.channel)"
                    :class="['channel-inline__icon', getChannelIcon(channel.channel)]"
                  />
                  <span class="channel-inline__name">{{ formatChannel(channel.channel) }}</span>
                </div>
                <div class="channel-inline__stats">
                  <span class="channel-inline__stat">
                    {{ t('campaignsBulkList.expansion.deliveries') }}: <strong>{{ formatInteger(channel.deliveries) }}</strong>
                  </span>
                  <span class="channel-inline__stat">
                    {{ t('campaignsBulkList.metrics.openRate') }}: <strong class="highlight">{{ formatPercentage(channel.open_rate) }}</strong>
                  </span>
                  <span class="channel-inline__stat">
                    {{ t('campaigns.common.metrics.openRateTotal') }}: <strong class="highlight">{{ formatPercentage(channel.open_rate_total) }}</strong>
                  </span>
                  <span class="channel-inline__stat">
                    {{ t('campaignsBulkList.metrics.clickRate') }}: <strong class="highlight">{{ formatPercentage(channel.click_rate) }}</strong>
                  </span>
                  <span class="channel-inline__stat">
                    {{ t('campaignsBulkList.metrics.salesAmount') }}: <strong>{{ formatAmount(channel.sales_amount) }}</strong>
                  </span>
                  <span class="channel-inline__stat">
                    {{ t('campaignsBulkList.expansion.revenuePerSend') }}: <strong>{{ formatAmount(channel.revenue_per_send) }}</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </SpmTableAPI>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import moment from 'moment-timezone';
import { useStore } from '@/store';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import { useRouter } from 'vue-router';
import { CampaignsBulkStatusEnum } from '@/types';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastSuccess, showToastError } from '@/helpers';
import { UserState } from '@/composables/User';
import {
  getChannelIcon,
  getChannelTagStyleVars,
  getStatusIcon,
  getStatusStyleVars,
} from '@/views/automated-scenarios/utils/styleTokens';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmMetricDisplay from '@/components/table-rest-api/SpmMetricDisplay.vue';
import SpmHelpIcon from '@/components/table-rest-api/SpmHelpIcon.vue';
import {
  formatPercentage as formatPercentageUtil,
  formatCurrency,
  formatInteger as formatIntegerUtil,
} from '@/components/table-rest-api/formatters';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SpmEmptyState from '@/components/table-rest-api/SpmEmptyState.vue';
import Dialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import ImportModal from '@/components/import/ImportModal.vue';
import CampaignsBulkBaseSelectorV2 from '@/components/automated-scenarios/CampaignsBulkBaseSelectorV2.vue';
import IntegrationOwnerBadge from '@/components/integrations/IntegrationOwnerBadge.vue';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  SpmActiveSort,
  SelectionInfo,
  ExportPayload,
  SpmTableAPIExposed,
} from '@/components/table-rest-api/types';
import { SpmFilterOperator } from '@/components/table-rest-api/filters';
import { isDemoModeActive, demoCampaigns } from '@/composables/DemoMode';
import {
  DuplicateBulkCampaignAction,
  DeleteBulkCampaignAction,
  ArchiveBulkCampaignAction,
  SuspendBulkCampaignAction,
  PublishToGalleryDialog,
  useBulkCampaignsActions,
} from './BulkCampaignsActions';
import type { BulkCampaignData } from './BulkCampaignsActions/types';

export default defineComponent({
  name: 'CampaignsBulkList',

  components: {
    SpmTableAPI,
    PageHeader,
    SpmBadge,
    SpmDateDisplay,
    SpmMetricDisplay,
    SpmHelpIcon,
    SpmButton,
    SpmEmptyState,
    Dialog,
    ConfirmDialog,
    ImportModal,
    CampaignsBulkBaseSelectorV2,
    IntegrationOwnerBadge,
    DuplicateBulkCampaignAction,
    DeleteBulkCampaignAction,
    ArchiveBulkCampaignAction,
    SuspendBulkCampaignAction,
    PublishToGalleryDialog,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();
    const confirm = useConfirm();
    const tableRef = ref<SpmTableAPIExposed | null>(null);

    // Watch flow editor visibility to reload table when editor closes
    const isFlowEditorVisible = computed(() => store.getters['flowEditor/isEditorVisible']);
    watch(isFlowEditorVisible, (newValue, oldValue) => {
      // Reload table when editor closes (visible -> hidden)
      if (oldValue === true && newValue === false && tableRef.value?.reloadData) {
        tableRef.value.reloadData();
      }
    });

    // Action component refs
    const duplicateRef = ref(null);
    const deleteRef = ref(null);
    const archiveRef = ref(null);
    const suspendRef = ref(null);
    const publishGalleryRef = ref<{ open: (list: { id: number; name: string }[]) => void; done: () => void } | null>(null);

    // Modal states
    const showNeedOfferModal = ref(false);
    const showImportModal = ref(false);
    const showCreateModal = ref(false);

    // Watch import modal visibility to reload table when editor closes
    watch(showImportModal, (newValue, oldValue) => {
      // Reload table when editor closes (visible -> hidden)
      if (oldValue === true && newValue === false && tableRef.value?.reloadData) {
        tableRef.value.reloadData();
      }
    });

    // Client timezone for date display
    const clientTimezone = moment.tz.guess() || 'UTC';

    // Hidden statuses (not shown in the list)
    const hiddenStatuses: CampaignsBulkStatusEnum[] = [
      CampaignsBulkStatusEnum.ARCHIVED,
      CampaignsBulkStatusEnum.ARCHIVED_BY_USER,
      CampaignsBulkStatusEnum.DELETED,
    ];

    // Status options for filter
    const statusOptions = computed(() => (Object.values(CampaignsBulkStatusEnum) as CampaignsBulkStatusEnum[])
      .filter((status) => !hiddenStatuses.includes(status))
      .map((status) => ({
        label: t(`campaignsBulkList.status.${status}`),
        value: status,
      })));

    // API call configuration.
    // `withStats=false` : le listing revient sans aucune métrique (rapide) ;
    // les colonnes `lazy` sont remplies ensuite via `loadCampaignsStats`.
    const apiCall = ref<ApiCallConfig>({
      type_api: NestApiType.CLIENT_API,
      path: '/campaigns/bulk?withStats=false',
      secure: true,
    });

    /**
     * Stats différées des colonnes lazy : un appel scopé sur les ids du lot,
     * réponse indexée par clé de ligne pour le merge dans le tableau.
     */
    const loadCampaignsStats = async (
      rows: Record<string, unknown>[],
    ): Promise<Record<string, Record<string, unknown>> | null> => {
      const ids = rows.map((row) => row.id).filter(Boolean);
      if (!ids.length) {
        return null;
      }

      const response = await nestCall(
        'get',
        NestApiType.CLIENT_API,
        '/campaigns/bulk/stats',
        {},
        '',
        undefined,
        true,
        { ids: ids.join(',') },
      );

      if (!response?.data || !Array.isArray(response.data)) {
        return null;
      }

      return Object.fromEntries(
        response.data.map(
          (stats: Record<string, unknown>): [string, Record<string, unknown>] => [String(stats.id), stats],
        ),
      );
    };

    // Persistent filters - exclude hidden statuses + demo mode filter
    const persistentFilters = computed<SpmApiFilter[]>(() => {
      const filters = hiddenStatuses.map((status) => ({
        field: 'status',
        operator: SpmFilterOperator.NOT_EQUALS,
        values: status,
      }));

      // In demo mode, only show selected campaigns (or none if empty)
      if (isDemoModeActive.value) {
        const ids = demoCampaigns.bulkIds;
        filters.push({
          field: 'id',
          operator: SpmFilterOperator.IN,
          values: (ids.length > 0 ? ids.join(',') : '0') as any,
        });
      }

      return filters;
    });

    // Initial sort - most recent first
    const initialSort: SpmActiveSort = {
      field: 'date_creation',
      order: 'desc',
    };

    // Columns configuration
    const columns = computed<SpmColumnConfig[]>(() => [
      {
        id: 'name',
        header: t('campaignsBulkList.columns.name'),
        maxWidth: '35%',
        fields: [
          {
            name: 'name',
            label: t('campaignsBulkList.columns.name'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'textInput' },
          },
          'id',
          {
            name: 'date_creation',
            label: t('campaignsBulkList.columns.createdAt'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'dateRange' },
          },
          {
            name: 'date_send',
            label: t('campaignsBulkList.columns.sentAt'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'dateRange' },
          },
          'primary_channel',
          'channels',
        ],
      },
      {
        id: 'status',
        header: t('campaignsBulkList.columns.status'),
        fields: [
          {
            name: 'status',
            label: t('campaignsBulkList.columns.status'),
            sortable: true,
            filterable: true,
            filterConfig: {
              type: 'singleSelect',
              options: statusOptions.value,
            },
          },
          'send_mode',
        ],
      },
      {
        id: 'performance',
        lazy: true,
        header: t('campaignsBulkList.columns.performance'),
        fields: [
          {
            name: 'open_rate',
            label: t('campaignsBulkList.metrics.openRate'),
            sortable: true,
          },
          {
            name: 'open_rate_total',
            label: t('campaigns.common.metrics.openRateTotal'),
            sortable: true,
          },
          {
            name: 'click_rate',
            label: t('campaignsBulkList.metrics.clickRate'),
            sortable: true,
          },
          'deliveries',
          'opens',
          'clicks',
        ],
      },
      {
        id: 'conversions',
        lazy: true,
        header: t('campaignsBulkList.columns.conversions'),
        fields: [
          {
            name: 'sales_amount',
            label: t('campaignsBulkList.metrics.salesAmount'),
            sortable: true,
          },
          {
            name: 'conversion_rate',
            label: t('campaignsBulkList.metrics.conversionRate'),
            sortable: true,
          },
          'sales',
          'bounces',
          'spam',
          'unsubscribes',
        ],
      },
    ]);

    /**
     * Reload table data after an action
     */
    const reloadTableData = () => {
      if (tableRef.value?.reloadData) {
        tableRef.value.reloadData();
      }
    };

    // Use the composable for actions
    const { rowActions, groupedActions } = useBulkCampaignsActions({
      duplicateRef,
      deleteRef,
      archiveRef,
      suspendRef,
      showNeedOfferModal,
      confirm,
      onRefresh: reloadTableData,
      onExport: (selection: SelectionInfo) => {
        console.log('Export selection:', selection);
        showToastSuccess(t('campaignsBulkList.export.success'));
      },
      onPublishToGallery: async (selection: SelectionInfo) => {
        if (selection.selectionCount === 0) return;

        try {
          setLoading(true);
          // Build query params based on selection mode
          const params: Record<string, any> = {
            fields: ['id', 'name'],
            limit: selection.selectionCount,
          };

          if (selection.selectAllMode) {
            // Select all mode: fetch all, exclude specific IDs if any
            if (selection.excludedRows.length > 0) {
              params.filters = [{ field: 'id', operator: 'not_in', values: selection.excludedRows.join(',') }];
            }
          } else {
            // Manual selection: fetch specific IDs
            params.filters = [{ field: 'id', operator: 'in', values: selection.selectedRows.join(',') }];
          }

          const response = await nestCall(
            'get',
            NestApiType.CLIENT_API,
            '/campaigns/bulk',
            {},
            '',
            undefined,
            true,
            params,
          );
          const campaignList = (response?.data || []).map((c: any) => ({ id: Number(c.id), name: c.name }));
          if (campaignList.length > 0 && publishGalleryRef.value?.open) {
            publishGalleryRef.value.open(campaignList);
          }
        } catch (error) {
          console.error('Publish to gallery error:', error);
          await showToastError(t('campaignsBulkList.groupedActions.publishToGalleryError'));
        } finally {
          setLoading(false);
        }
      },
    });

    /**
     * Reset table selection after a grouped action is confirmed
     */
    const resetTableSelection = () => {
      if (tableRef.value?.unselectAll) {
        tableRef.value.unselectAll();
      }
    };

    // Handler for action confirmations
    const onActionConfirm = () => {
      resetTableSelection();
      reloadTableData();
    };

    // Handler for publish to gallery dialog confirmation
    const onPublishToGalleryConfirm = async (payload: { campaignIds: number[]; campaignIdsWithoutTemplates: number[] }) => {
      try {
        setLoading(true);
        const response = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/data/publish-to-gallery',
          {},
          '',
          {
            shopId: UserState.activeShop?.id,
            campaignIds: payload.campaignIds,
            campaignIdsWithoutTemplates: payload.campaignIdsWithoutTemplates,
            type: 'campaign_bulk',
          },
          true,
        );
        if (response?.success) {
          await showToastSuccess(t('campaignsBulkList.groupedActions.publishToGallerySuccess'));
        } else {
          await showToastError(response?.message || t('campaignsBulkList.groupedActions.publishToGalleryError'));
        }
      } catch (error) {
        console.error('Publish to gallery error:', error);
        await showToastError(t('campaignsBulkList.groupedActions.publishToGalleryError'));
      } finally {
        if (publishGalleryRef.value?.done) {
          publishGalleryRef.value.done();
        }
        resetTableSelection();
        setLoading(false);
      }
    };

    // Navigate to MyOffer page
    const goToMyOffer = async () => {
      await router.push({ name: 'shop.MyOffer' });
    };

    const setLoading = async (loading: boolean) => {
      if (tableRef.value) {
        await tableRef.value.setLoading(loading);
      }
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        setLoading(true);
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          selection_type: payload.selectAllMode ? 'all' : 'manual',
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/export/init',
          {},
          '',
          exportRequest,
          true,
        );

        if (response && !response.error) {
          await showToastSuccess(t('campaignsBulkList.export.success'));
        } else {
          await showToastSuccess(t('campaignsBulkList.export.error'));
        }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('campaignsBulkList.export.error'));
      } finally {
        resetTableSelection();
        reloadTableData();
        setLoading(false);
      }
    };

    // Formatting functions - using shared formatters
    const formatPercentage = (value: number | null | undefined): string => formatPercentageUtil(value, false);

    const formatAmount = (value: number | null | undefined): string => formatCurrency(value, 'EUR');

    // Tooltip for the date_modification badge: same DB field, different
    // semantic depending on the campaign status (suspended → stop date,
    // anything else → last edit).
    const getModificationTooltip = (status: string | null | undefined): string => {
      const isSuspended =
        status === 'suspended' || status === 'suspended_by_user';
      return isSuspended
        ? t('campaignsBulkList.dateTooltips.suspension')
        : t('campaignsBulkList.dateTooltips.modification');
    };

    const formatChannel = (value: string | null): string => {
      if (!value) return '-';
      const key = value.toLowerCase();
      const translationKey = `campaignsBulkList.channels.${key}`;
      const translated = t(translationKey);
      return translated === translationKey ? key : translated;
    };

    const extractChannelTags = (row: BulkCampaignData): string[] => {
      const uniqueChannels = new Set<string>();

      if (Array.isArray(row?.channels)) {
        row.channels.forEach((channel) => {
          if (channel?.channel) {
            uniqueChannels.add(channel.channel);
          }
        });
      }

      if (row?.primary_channel) {
        uniqueChannels.add(row.primary_channel);
      }

      return Array.from(uniqueChannels);
    };

    // Formatting functions for expansion - using shared formatters
    const formatInteger = (value: number | null | undefined): string => formatIntegerUtil(value);

    // Style functions - convert CSS vars format to SpmBadge format
    const getStatusStyle = (status: string | null | undefined) => {
      const vars = getStatusStyleVars(status);
      return {
        bg: vars['--status-bg'] || 'var(--surface-200)',
        color: vars['--status-color'] || 'var(--text-color)',
        borderColor: vars['--status-border'],
      };
    };
    const sendModeIcon = (mode: string): string => {
      if (mode === 'perfect_timing') return 'fa-light fa-clock';
      if (mode === 'slow_down') return 'fa-light fa-gauge-low';
      return '';
    };

    const sendModeLabel = (row: BulkCampaignData): string => {
      if (!row.send_mode || row.send_mode === 'standard') return '';
      const baseLabel = t(`campaignsBulkList.sendMode.${row.send_mode}.label`);
      if (row.send_mode === 'slow_down' && row.interval_between_sends) {
        return t('campaignsBulkList.sendMode.slow_down.labelWithInterval', {
          label: baseLabel,
          minutes: row.interval_between_sends,
        });
      }
      return baseLabel;
    };

    const sendModeTooltip = (row: BulkCampaignData): string => {
      if (!row.send_mode || row.send_mode === 'standard') return '';
      const baseTooltip = t(`campaignsBulkList.sendMode.${row.send_mode}.tooltip`);
      if (row.send_mode === 'slow_down' && row.interval_between_sends) {
        return t('campaignsBulkList.sendMode.slow_down.tooltipWithInterval', {
          tooltip: baseTooltip,
          minutes: row.interval_between_sends,
        });
      }
      return baseTooltip;
    };

    const getChannelTagStyle = (channel: string | null | undefined) => {
      const vars = getChannelTagStyleVars(channel);
      return {
        bg: vars['--channel-bg'] || 'var(--surface-200)',
        color: vars['--channel-color'] || 'var(--text-color)',
        borderColor: vars['--channel-border'],
      };
    };

    return {
      t,
      tableRef,
      apiCall,
      loadCampaignsStats,
      columns,
      persistentFilters,
      initialSort,
      rowActions,
      groupedActions,
      // Action component refs
      duplicateRef,
      deleteRef,
      archiveRef,
      suspendRef,
      publishGalleryRef,
      onActionConfirm,
      onPublishToGalleryConfirm,
      // Modal states
      showNeedOfferModal,
      showImportModal,
      showCreateModal,
      goToMyOffer,
      // Formatting functions
      formatPercentage,
      formatAmount,
      formatInteger,
      formatChannel,
      extractChannelTags,
      getModificationTooltip,
      // Style functions
      getStatusStyle,
      getStatusIcon,
      getChannelTagStyle,
      getChannelIcon,
      handleExport,
      clientTimezone,
      sendModeIcon,
      sendModeLabel,
      sendModeTooltip,
    };
  },
});
</script>

<style lang="scss" scoped>
/* Page layout */
.campaigns-bulk-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* Campaign info styling */
.campaign-info {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.campaign-name {
  font-weight: 500;
  color: var(--text-color);
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}

.campaign-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

/* Channel tags */
.campaign-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.25rem;
}

/* Metrics display */
.metrics-display {
  display: grid;
  grid-template-columns: max-content max-content;
  align-items: center;
  gap: 0.375rem 0.5rem;

  // Labels and values join the grid so every value lines up after the longest label.
  :deep(.spm-metric-display) {
    display: contents;
  }
}

/* Expansion Container */
.expansion-container {
  padding: 1.25rem;
  background: linear-gradient(135deg, var(--surface-50) 0%, var(--surface-100) 100%);
  border-radius: 8px;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: var(--surface-0);
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid var(--surface-200);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

.summary-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  font-size: 1.25rem;
}

.summary-card--primary .summary-card__icon {
  background: rgba(59, 130, 246, 0.12);
  color: #3b82f6;
}

.summary-card--success .summary-card__icon {
  background: rgba(34, 197, 94, 0.12);
  color: #22c55e;
}

.summary-card--info .summary-card__icon {
  background: rgba(14, 165, 233, 0.12);
  color: #0ea5e9;
}

.summary-card--warning .summary-card__icon {
  background: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

.summary-card__content {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.summary-card__value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
  line-height: 1.2;
}

.summary-card__label {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

/* Compact Metrics Row */
.metrics-row {
  display: flex;
  gap: 0.5rem;
}

.metric-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-200);
  border-radius: 4px;
}

.metric-item__label {
  font-size: 0.6rem;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.02em;
  text-align: center;
  line-height: 1.2;
  margin-bottom: 0.25rem;
}

.metric-item__value {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color);
}

.metric-item--warning .metric-item__value {
  color: #f59e0b;
}

.metric-item--danger .metric-item__value {
  color: #ef4444;
}

/* Channels Inline Display */
.channels-inline {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.channel-inline {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0.75rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-200);
  border-radius: 4px;
}

.channel-inline__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 100px;
}

.channel-inline__icon {
  font-size: 0.875rem;
  color: var(--primary-color);
}

.channel-inline__name {
  font-weight: 600;
  font-size: 0.8rem;
  color: var(--text-color);
}

.channel-inline__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.channel-inline__stat {
  font-size: 0.75rem;
  color: var(--text-color-secondary);

  strong {
    color: var(--text-color);
    font-weight: 600;

    &.highlight {
      color: var(--primary-color);
    }
  }
}

.status-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.4rem;
}

.send-mode-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.6rem;
  border-radius: 6px;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.01em;

  &--perfect_timing {
    background: #eff6ff;
    color: #2563eb;
    border: 1px solid #bfdbfe;
  }

  &--slow_down {
    background: #fef3c7;
    color: #d97706;
    border: 1px solid #fde68a;
  }

  i {
    font-size: 0.72rem;
  }
}
</style>
